/**
 * Processes HTML content and wraps specified words/phrases with tooltip spans
 * Tooltip words are fetched from server via useTooltipWords hook
 */

export interface TooltipSection {
  title?: string;
  content: string;
  type?: "info" | "positive" | "negative" | "warning";
}

export interface TooltipWord {
  id?: string;
  phrase: string;
  title?: string;
  explanation: string;
  synonyms?: string[]; // Alternative phrases that should show the same tooltip
  keywords?: { phrase: string; explanation: string }[]; // Deprecated: keywords are now automatically detected from tooltip registry
}

// Tooltip words registry - populated by useTooltipWords hook
let TOOLTIP_WORDS: TooltipWord[] = [];

/**
 * Sets the tooltip words registry (called by useTooltipWords hook)
 */
export function setTooltipWords(words: TooltipWord[]): void {
  TOOLTIP_WORDS = words;
  // Clear cache when words change
  cachedTooltipPatterns = null;
}

/**
 * Gets the current tooltip words
 */
export function getTooltipWords(): TooltipWord[] {
  return TOOLTIP_WORDS;
}

// Tags that should be excluded from tooltip processing
// Only show tooltips in regular paragraph text, not in special elements
const EXCLUDED_TAGS = new Set([
  "SCRIPT",
  "STYLE",
  // Headings
  "H1",
  "H2",
  "H3",
  "H4",
  "H5",
  "H6",
  // Quotes and citations
  "BLOCKQUOTE",
  "CITE",
  "Q",
  // Figure elements
  "FIGCAPTION",
  "CAPTION",
  // Other special text
  "PRE",
  "CODE",
  "SUMMARY",
]);

/**
 * Escapes special regex characters in a string
 */
function escapeRegex(str: string): string {
  return str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

/**
 * Preserves whitespace at the boundaries of text fragments created
 * during tooltip processing. When a text node is split by tooltip spans,
 * leading/trailing spaces in the resulting fragments can be collapsed by
 * CSS in flex containers (anonymous flex items trim edge whitespace).
 * Converting boundary spaces to non-breaking spaces (\u00A0) prevents this.
 */
function preserveWhitespace(text: string): string {
  if (text.length === 0) return text;

  // Whitespace-only: preserve all spaces
  if (text.trim() === "") {
    return text.replace(/ /g, "\u00A0");
  }

  // Preserve leading and trailing spaces adjacent to tooltip spans
  let result = text;
  if (result.startsWith(" ")) {
    result = "\u00A0" + result.substring(1);
  }
  if (result.endsWith(" ")) {
    result = result.substring(0, result.length - 1) + "\u00A0";
  }
  return result;
}

// CSS classes that should be excluded from tooltip processing
const EXCLUDED_CLASSES = [
  "tooltip-word",
  "quote",
  "blockquote",
  "citation",
  "pullquote",
  "highlight-quote",
  "featured-text",
  "intro-text",
  "lead",
];

/**
 * Checks if an element or any of its ancestors should be excluded from tooltip processing
 */
function isExcludedElement(element: Element): boolean {
  let current: Element | null = element;
  while (current) {
    // Check excluded tags
    if (EXCLUDED_TAGS.has(current.tagName)) {
      return true;
    }
    // Check excluded classes
    for (const className of EXCLUDED_CLASSES) {
      if (current.classList.contains(className)) {
        return true;
      }
    }
    current = current.parentElement;
  }
  return false;
}

/**
 * Gets all tooltip phrases from the registry
 * Used for automatic keyword detection in tooltip content
 */
export function getAllTooltipPhrases(): string[] {
  return TOOLTIP_WORDS.map((tw) => tw.phrase);
}

/**
 * Gets tooltip patterns with regex for matching
 * Includes both main phrases and synonyms
 * Cached for performance
 */
let cachedTooltipPatterns: Array<{ phrase: string; regex: RegExp }> | null =
  null;

export function getTooltipPatterns(): Array<{ phrase: string; regex: RegExp }> {
  if (!cachedTooltipPatterns) {
    cachedTooltipPatterns = [];

    for (const tw of TOOLTIP_WORDS) {
      // Add main phrase
      cachedTooltipPatterns.push({
        phrase: tw.phrase,
        regex: new RegExp(`\\b${escapeRegex(tw.phrase)}\\b`, "gi"),
      });

      // Add synonyms (they map to the same tooltip word)
      if (tw.synonyms && tw.synonyms.length > 0) {
        for (const synonym of tw.synonyms) {
          cachedTooltipPatterns.push({
            phrase: synonym,
            regex: new RegExp(`\\b${escapeRegex(synonym)}\\b`, "gi"),
          });
        }
      }
    }

    // Sort by length (longest first) to match longer phrases before shorter ones
    // This prevents "penningtvätt" from matching before "penningtvätt och terrorfinansiering"
    cachedTooltipPatterns.sort((a, b) => b.phrase.length - a.phrase.length);
  }
  return cachedTooltipPatterns;
}

/**
 * Result from processing HTML with tooltips
 * Includes the processed HTML and a list of unique phrases found
 */
export interface ProcessedHTMLResult {
  html: string;
  uniquePhrases: TooltipWord[];
}

/**
 * Processes HTML string and wraps matching phrases with tooltip spans
 * Returns the processed HTML string with data attributes for tooltips
 * Handles both main phrases and synonyms
 * Progressive disclosure: tracks occurrence count per phrase
 */
export function processHTMLWithTooltips(html: string): string {
  const result = processHTMLWithTooltipsAndPhrases(html);
  return result.html;
}

/**
 * Processes HTML and returns both the processed HTML and unique phrases found
 * Used for glossary footer functionality
 */
export function processHTMLWithTooltipsAndPhrases(
  html: string
): ProcessedHTMLResult {
  if (!html || typeof html !== "string") {
    return { html, uniquePhrases: [] };
  }

  // Use DOM parsing for more reliable HTML manipulation
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;

  // Track occurrence count per tooltip word (using main phrase as key)
  const occurrenceCount: Map<string, number> = new Map();
  // Track unique tooltip words found
  const foundTooltipWords: Set<string> = new Set();

  // Build a list of all phrases to match (including synonyms) with their tooltip data
  const phrasesToMatch: Array<{
    phrase: string;
    tooltipWord: TooltipWord;
    regex: RegExp;
  }> = [];

  for (const tooltipWord of TOOLTIP_WORDS) {
    // Add main phrase
    phrasesToMatch.push({
      phrase: tooltipWord.phrase,
      tooltipWord,
      regex: new RegExp(`\\b${escapeRegex(tooltipWord.phrase)}\\b`, "gi"),
    });

    // Add synonyms
    if (tooltipWord.synonyms && tooltipWord.synonyms.length > 0) {
      for (const synonym of tooltipWord.synonyms) {
        phrasesToMatch.push({
          phrase: synonym,
          tooltipWord,
          regex: new RegExp(`\\b${escapeRegex(synonym)}\\b`, "gi"),
        });
      }
    }
  }

  // Sort by phrase length (longest first)
  phrasesToMatch.sort((a, b) => b.phrase.length - a.phrase.length);

  // Process each phrase
  for (const { tooltipWord, regex } of phrasesToMatch) {
    // Use main phrase as key for tracking occurrences (synonyms share count with main phrase)
    const phraseKey = tooltipWord.phrase.toLowerCase();

    // Walk through text nodes and replace matches
    const walker = document.createTreeWalker(
      tempDiv,
      NodeFilter.SHOW_TEXT,
      null
    );

    const textNodes: Text[] = [];
    let node: Node | null;
    while ((node = walker.nextNode())) {
      const textNode = node as Text;
      const parent = textNode.parentElement;

      // Skip if parent is an excluded tag (headings, script, style) or already a tooltip span
      if (parent && isExcludedElement(parent)) {
        continue;
      }

      if (regex.test(textNode.textContent || "")) {
        textNodes.push(textNode);
      }
    }

    // Process each matching text node
    for (const textNode of textNodes) {
      const text = textNode.textContent || "";
      const matches: Array<{ index: number; length: number; text: string }> =
        [];

      // Reset regex
      regex.lastIndex = 0;
      let match;
      while ((match = regex.exec(text)) !== null) {
        matches.push({
          index: match.index,
          length: match[0].length,
          text: match[0],
        });
      }

      if (matches.length === 0) {
        continue;
      }

      // Build replacement nodes
      const fragment = document.createDocumentFragment();
      let lastIndex = 0;

      for (const match of matches) {
        // Add text before match
        if (match.index > lastIndex) {
          const beforeText = text.substring(lastIndex, match.index);
          fragment.appendChild(
            document.createTextNode(preserveWhitespace(beforeText))
          );
        }

        // Track this tooltip word was found
        foundTooltipWords.add(phraseKey);

        // Get and increment occurrence count for progressive disclosure
        const currentOccurrence = (occurrenceCount.get(phraseKey) || 0) + 1;
        occurrenceCount.set(phraseKey, currentOccurrence);

        // Create tooltip span for match
        const span = document.createElement("span");
        span.className = "tooltip-word";
        // Use the matched text as the phrase attribute (preserves case)
        span.setAttribute("data-tooltip-phrase", match.text);
        // Store explanation from the tooltip word
        const explanationData =
          typeof tooltipWord.explanation === "string"
            ? tooltipWord.explanation
            : JSON.stringify(tooltipWord.explanation);
        span.setAttribute(
          "data-tooltip",
          explanationData.replace(/"/g, "&quot;")
        );
        // Add occurrence number for progressive disclosure styling
        span.setAttribute("data-tooltip-occurrence", String(currentOccurrence));
        span.textContent = match.text;
        fragment.appendChild(span);

        lastIndex = match.index + match.length;
      }

      // Add remaining text
      if (lastIndex < text.length) {
        const remainingText = text.substring(lastIndex);
        fragment.appendChild(
          document.createTextNode(preserveWhitespace(remainingText))
        );
      }

      // Replace text node with fragment
      if (textNode.parentNode) {
        textNode.parentNode.replaceChild(fragment, textNode);
      }
    }
  }

  // Build list of unique tooltip words found (in order of first appearance)
  const uniquePhrases: TooltipWord[] = [];
  for (const phraseKey of foundTooltipWords) {
    const tooltipWord = TOOLTIP_WORDS.find(
      (tw) => tw.phrase.toLowerCase() === phraseKey
    );
    if (tooltipWord) {
      uniquePhrases.push(tooltipWord);
    }
  }

  return { html: tempDiv.innerHTML, uniquePhrases };
}

/**
 * Gets tooltip data for a phrase (searches both main phrases and synonyms)
 */
export function getTooltipData(phrase: string): TooltipWord | null {
  const lowerPhrase = phrase.toLowerCase();

  return (
    TOOLTIP_WORDS.find((tw) => {
      // Check main phrase
      if (tw.phrase.toLowerCase() === lowerPhrase) {
        return true;
      }
      // Check synonyms
      if (tw.synonyms && tw.synonyms.length > 0) {
        return tw.synonyms.some((syn) => syn.toLowerCase() === lowerPhrase);
      }
      return false;
    }) || null
  );
}

/**
 * Gets tooltip explanation for a phrase (legacy support)
 */
export function getTooltipExplanation(phrase: string): string | null {
  const tooltipWord = getTooltipData(phrase);
  if (!tooltipWord) return null;

  return tooltipWord.explanation;
}
